import React, { useEffect, useState } from "react";
import { errorNotification } from "../Generic/Notifications";
import EmptyState from "../Generic/EmptyState";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Loader from "../Generic/Loader";
import InventoryPlanService from "./Services/InventoryPlanService";
import StackedBarChart from "../Generic/StackedBarChart";
import Button from "../Generic/Button";
import { AppRoutesConfig } from "../config/app.config";
import { beautifyKey } from "../util/util";
import TabSelector from "../Generic/TabSelector";

const GROUPS = {
	Expired: "expired",
	Locked: "locked",
	Active: "active",
	Undocked: "undocked",
	BelowThresholdAmount: "belowThresholdAmount",
	Offine: "offline"
};

const GROUP_CONTENT = {
	[GROUPS.Expired]: "Expired",
	[GROUPS.Locked]: "Locked",
	[GROUPS.Active]: "Active",
	[GROUPS.Undocked]: "Undocked",
	[GROUPS.BelowThresholdAmount]: "Below Threshold",
	[GROUPS.Offine]: "Offline"
};

const GROUP_STYLE = {
	[GROUPS.Active]: {
		outer: {
			backgroundColor: "#b6cedc",
			borderColor: "#209cb9",
		},
		inner: {
			backgroundColor: "#c1e3eb",
			borderColor: "#209cb9",
		},
	},
	[GROUPS.Expired]: {
		outer: {
			backgroundColor: "#d8d8d8",
			borderColor: "#a8a8a8",
		},
		inner: {
			backgroundColor: "#f5f5f5",
			borderColor: "#a8a8a8",
		},
	},
	[GROUPS.Locked]: {
		outer: {
			backgroundColor: "#d8d8d8",
			borderColor: "#a8a8a8",
		},
		inner: {
			backgroundColor: "#f5f5f5",
			borderColor: "#a8a8a8",
		},
	},
	[GROUPS.Undocked]: {
		outer: {
			backgroundColor: "#d8d8d8",
			borderColor: "#a8a8a8",
		},
		inner: {
			backgroundColor: "#f5f5f5",
			borderColor: "#a8a8a8",
		},
	},
	[GROUPS.Offine]: {
		outer: {
			backgroundColor: "#d8d8d8",
			borderColor: "#a8a8a8",
		},
		inner: {
			backgroundColor: "#f5f5f5",
			borderColor: "#a8a8a8",
		},
	},
	[GROUPS.BelowThresholdAmount]: {
		outer: {
			backgroundColor: "#d8d8d8",
			borderColor: "#a8a8a8",
		},
		inner: {
			backgroundColor: "#d7cacf",
			borderColor: "#aa7074",
		},
	}
};

const INFO_MESSAGES_BY_TAB_ID = {
    Bowls: "* Total usable number of bowls would be 1 less than what you have filled.",
};

const Page = styled.div`
	display: flex;
	height: max-content;
	overflow-y: auto;
	overflow-x: hidden;
	flex-direction: column;
	text-align: left;
	width: 100%;
	padding: 1.5rem;
	gap: 1rem;
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	font-weight: 600;
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin};
`;

const Block = styled.div`
	width: 100%;
	height: auto;
	display: flex;
	gap: ${(props) => props.gap || "1rem"};
	margin: ${(props) => props.margin};
	align-items: center;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	flex-wrap: ${(props) => props.flexWrap};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

function dataParser(data) {
	if (!data) return;

	const { units, stepCount, maxValue, histogramDetails, xAxisTitle, yAxisTitle } = data;

	const inner = {
		data: [],
		label: "Available Quantity",
		tooltipData: [],
		datalabelData: [],
		datalabels: {
			anchor: "end",
			align: "bottom",
			formatter: (value, context) => {
				const dataLabelData = context?.dataset?.datalabelData?.[context.dataIndex];
				return dataLabelData || "";
			},
		},
	};

	const outer = {
		data: [],
		label: "Filled Quantity",
		tooltipData: [],
		datalabelData: [],
		datalabels: {
			anchor: "end",
			align: "top",
			formatter: (value, context) => {
				const dataLabelData = context?.dataset?.datalabelData?.[context.dataIndex];
				return dataLabelData || "";
			},
		},
	};

	const datasets = [inner, outer];
	const labels = [];
	const tooltipParsers = {
		label: (context) => "",
		afterLabel: (context) => {
			const metadata = context?.dataset?.tooltipData?.[context.dataIndex];
			if (!metadata) return "";
			const { expiresAt, lastFilled, filledQuantity, availableQuantity, group } = metadata;
			return (
				GROUP_CONTENT[group] + 
				"\n\nReplenished at " +
				lastFilled +
				"\nExpires at " +
				expiresAt +
				"\n\nAvailable Amount " +
				availableQuantity +
				"\nFilled Amount " +
				filledQuantity
			);
		},
	};

	const customOptions = {
		scales: {
			x: {
				grid: {
					display: false
				},
				stacked: true,
				title: {
					display: true,
					text: xAxisTitle,
				},
			},
			y: {
				grid: {
					display: false
				},
				stacked: true,
				title: {
					display: true,
					text: yAxisTitle,
				},
				min: 0,
				max: maxValue,
				ticks: {
					stepSize: stepCount,
				},
			},
		},
		plugins: {
			legend: {
				onClick: null,
				labels: {
					generateLabels: function(chart) {
						return [
							{
								text: 'Available Quantity',
								fillStyle: GROUP_STYLE[GROUPS.Active].inner.backgroundColor,
								strokeStyle: GROUP_STYLE[GROUPS.Active].inner.borderColor,
								lineWidth: 1
							},
							{
								text: 'Filled Quantity',
								fillStyle: GROUP_STYLE[GROUPS.Active].outer.backgroundColor,
								strokeStyle: GROUP_STYLE[GROUPS.Active].outer.borderColor,
								lineWidth: 1
							}
						];
					}
				}
			}
		}
	};

	for (const index in histogramDetails) {
		const { histogramXAxis, availableAmount, filledAmount, expiresAt, replenishedAt, group, belowThresholdAmount } = histogramDetails[index];

		let itemGroup = group || GROUPS.Expired;

		if(belowThresholdAmount) {
			itemGroup = GROUPS.BelowThresholdAmount;
		}

		labels.push(histogramXAxis);

		outer.tooltipData.push({
			itemName: histogramXAxis,
			filledQuantity: `${filledAmount} ${units}`,
			availableQuantity: `${availableAmount} ${units}`,
			lastFilled: dateToString(replenishedAt),
			expiresAt: dateToString(expiresAt),
			group,
		});

		if (availableAmount !== filledAmount) {
			inner.datalabelData[index] = availableAmount;
		}
		outer.datalabelData[index] = filledAmount;

		outer.data.push(filledAmount - availableAmount);
		inner.data.push(availableAmount);

		const groupStyleOverrides = GROUP_STYLE[itemGroup];
		if (groupStyleOverrides) {
			outer.backgroundColor = outer.backgroundColor || [];
			outer.borderColor = outer.borderColor || [];
			inner.backgroundColor = inner.backgroundColor || [];
			inner.borderColor = inner.borderColor || [];
			outer.backgroundColor[index] = groupStyleOverrides.outer.backgroundColor;
			outer.borderColor[index] = groupStyleOverrides.outer.borderColor;
			inner.backgroundColor[index] = groupStyleOverrides.inner.backgroundColor;
			inner.borderColor[index] = groupStyleOverrides.inner.borderColor;
		}
	}

	return { datasets, labels, tooltipParsers, customOptions };
}

function dateToString(date) {
	return new Date(date).toLocaleString();
}

function renderChart(data, activetabId, setActiveTab) {
	if (!activetabId || !data) return;

	let activetabData = data[activetabId],
		renderData = [],
		sections = [];

	for (const sectionId in data) {
		let sectionName = data[sectionId].sectionName || beautifyKey(sectionId);
		sections.push({ id: sectionId, label: sectionName });
	}

	renderData.push(<TabSelector tabs={sections} onChange={setActiveTab} activeTabId={activetabId} />);

	if (activetabData) {
		try {
			const props = dataParser(activetabData);
			const infoMessage = INFO_MESSAGES_BY_TAB_ID[activetabId] ??  ""
			renderData.push(<StackedBarChart {...props} infoMessage={infoMessage} />);
		} catch (error) {
			renderData.push(<EmptyState />);
		}
	} else {
		renderData.push(<EmptyState />);
	}
	return renderData;
}

const CurrentInventory = (props) => {
	const { machineID } = props;
	const [inventoryData, setInventoryData] = useState({});
	const [error, setError] = useState(false);
	const [activeTab, setActiveTab] = useState("");

	const [loading, setLoading] = useState(true);

	async function getCurrentInventoryGraphData() {
		setLoading(true);
		const response = await InventoryPlanService.getCurrentInventoryGraph(machineID);
		const { data, error } = response;
		if (data) {
			const inventoryData = data.inventorySections;
			if (inventoryData) {
				const firstSection = Object.keys(inventoryData)[0];
				setActiveTab(firstSection);
				setInventoryData(inventoryData);
			} else {
				errorNotification("Insufficent Data Received");
				setError(true);
			}
		}
		if (error) {
			errorNotification(error);
			setError(true);
		}
		setLoading(false);
	}

	useEffect(() => {
		getCurrentInventoryGraphData();
	}, []);

	return (
		<Page>
			<Block>
				<Header>Inventory Plans</Header>

				<Div gap="1rem" margin="0 0 0 auto">
					<Button onClick={getCurrentInventoryGraphData} disabled={loading}>
						{" "}
						Get Latest Snapshot
					</Button>
				</Div>

				<Link to={`/${machineID}/${AppRoutesConfig.MACHINE.INVENTORY_PLAN.CREATE.path}?fill_with_current_inventory=true`}>
					<Button disabled={loading}>Fill with Current Inventory</Button>
				</Link>
			</Block>
			{!loading && !error ? renderChart(inventoryData, activeTab, setActiveTab) : ""}
			{loading ? (
				<CenteredContainer>
					<Loader />
				</CenteredContainer>
			) : (
				""
			)}

			{error && !loading ? (
				<CenteredContainer>
					<EmptyState retry={getCurrentInventoryGraphData} message="Something Went wrong!" />
				</CenteredContainer>
			) : (
				""
			)}
		</Page>
	);
};

const mapStateToProps = (state) => {
	const { landing } = state;
	return { ...landing };
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators({}, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(CurrentInventory);
